import Appsignal from '@appsignal/javascript'
import axios, { AxiosError } from 'axios'
import { inject } from 'vue'

const PART_SEPARATOR = ' - '

const joinPresent = (...parts: (string | undefined)[]) =>
  parts.filter((part) => part?.trim()).join(PART_SEPARATOR)

export const useHandleError = () => {
  // Use global instance provided during app initialization
  const appsignal: Appsignal | undefined = inject('appsignal')

  const logError = (
    error: Error | unknown,
    message: string,
    overwriteAction?: string, // for grouping errors in appsignal
    sendToAppSignal = true
  ) => {
    let errorNotNull: Error
    if (error instanceof Error) {
      errorNotNull = error
    } else {
      // Not sure the best way to handle non-Error exceptions and keep stack trace.
      // This a workaround to make sure they are still reported until we find a better way
      errorNotNull = new Error(message)
    }

    let additionalData: string | undefined
    if (axios.isAxiosError(errorNotNull)) {
      additionalData = handleAxiosError(errorNotNull, message)
    } else {
      handleUnexpectedError(errorNotNull, message)
    }

    if (sendToAppSignal) {
      errorNotNull.message = joinPresent(
        message,
        errorNotNull.message,
        additionalData
      )

      let fileName = overwriteAction
      if (!fileName) {
        // get codebase filename from stack trace
        errorNotNull.stack?.split('\n').some((line) => {
          const words = line?.trim().split(' ') ?? []
          const filePath = words.find((word) => word.includes('/src/'))
          if (filePath) {
            const baseFilePathPattern = /(http\S*\/src\/)/i
            fileName = filePath.replace(baseFilePathPattern, '@/')
          }
          return !!fileName
        })
      }
      if (!fileName) {
        // if codebase filename not found, default to first line of stacktrace
        fileName = errorNotNull.stack?.split('\n')[2]?.trim().split(' ')[1]
      }
      appsignal?.sendError(errorNotNull, (span) => {
        if (fileName?.length) {
          span.setAction(fileName)
        }
      })
    }
  }

  return { logError }
}

const handleAxiosError = (
  error: AxiosError,
  message: string
): string | undefined => {
  console.warn(`--- An API error occurred ---`)
  console.error(message, error)

  // Return URL for easier appsignal tracing
  return error.request?.responseURL
}

const handleUnexpectedError = (error: Error, message: string) => {
  // Add unexpected error handling here
  console.warn(`--- An unexpected error occurred ---`)
  console.error(message, error)
}
